import React, { useState } from "react";
import i18n from "@microdoing/shared/i18n";
import {
  changeActionState,
  changeRating,
} from "@microdoing/shared/services/action/actions";
import { getRating } from "@microdoing/shared/services/action/selectors";
import {
  getCampaignSelected,
  getTeam,
  getUserCampaignRank,
  getUserCampaignTeamRank,
} from "@microdoing/shared/services/userCampaign/selectors";
import {
  Box,
  MainButton,
  MainText,
  MySafeAreaView,
} from "app/components/common";
import { useDispatch, useSelector } from "react-redux";
import RatingStar from "./RatingStar";
import { useNavigation } from "@react-navigation/native";
import AllStarsAnimated from "./AllStarsAnimated";
import HeaderScreen from "app/components/common/HeaderScreen";
import theme from "app/Theme/Theme";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import WebDesktopView from "app/components/common/Web/WebDesktopView";
import { breadCrumbsReflexiveAnalysisRatingScreen } from "app/navigation/breadcrumb/BreadcrumbPaths";

export interface IActionRatingScreenProps {}

const ActionRatingScreen = ({}: IActionRatingScreenProps) => {
  const team = useSelector(getTeam);
  const teamRank = useSelector(getUserCampaignTeamRank);
  const userRank = useSelector(getUserCampaignRank);
  const campaignSelected = useSelector(getCampaignSelected);
  const rating = useSelector(getRating);

  const [previousStarSelected, setPreviousStarSelected] = useState(rating);

  const { points } = campaignSelected;
  const teamPoints = team ? team.points : 0;

  const dispatch = useDispatch();
  const navigation = useNavigation();

  const onStarRatingPress = rate => {
    setPreviousStarSelected(rating);
    dispatch(
      changeActionState({
        oldTeamPoints: teamPoints,
        oldUserPoints: points,
        oldRank: userRank,
        oldTeamRank: teamRank,
      }),
    );

    dispatch(changeRating(rate));
  };

  return (
    <WebDesktopView breadcrumbsPath={breadCrumbsReflexiveAnalysisRatingScreen}>
      <MySafeAreaView
        flex={1}
        borderRadius={isWebDesktop ? 8 : 0}
        padding={isWebDesktop ? "xl" : "null"}
        paddingHorizontal={isWebDesktop ? "xxl" : "sl"}
        paddingTop={isWebDesktop ? "null" : "s"}
        backgroundColor="neutral.white"
        zIndex={0}
        alignItems="center"
        overflow="visible"
      >
        {!isWebDesktop && (
          <HeaderScreen title={i18n.t("action-rating")} leftIcon />
        )}
        <Box flex={1} width={isWebDesktop ? "45%" : "100%"}>
          <Box flex={1}>
            <Box flex={1} justifyContent="flex-start" alignItems="center">
              <Box flex={1} alignItems="center" justifyContent="center">
                <AllStarsAnimated
                  previousStarSelected={previousStarSelected}
                  rating={rating}
                />
              </Box>
              <Box marginBottom="xs">
                {!!rating ? (
                  <MainText variant="l5-bold" textAlign="center">
                    {i18n.t(`rating-text-${rating}`)}
                  </MainText>
                ) : (
                  <MainText variant="l5-bold" textAlign="center">
                    {i18n.t("action-rating-question")}
                  </MainText>
                )}
              </Box>
              <MainText
                color="neutral.600"
                variant="l4-medium"
                textAlign="center"
              >
                {i18n.t("action-rating-explanation")}
              </MainText>
            </Box>
          </Box>
          <Box
            flexDirection="column"
            marginTop="l"
            marginBottom="s"
            marginHorizontal="s"
          >
            <Box flexDirection="row" justifyContent="space-between">
              <RatingStar
                isFill={rating >= 1}
                setRating={() => onStarRatingPress(1)}
              />
              <RatingStar
                isFill={rating >= 2}
                setRating={() => onStarRatingPress(2)}
              />
              <RatingStar
                isFill={rating >= 3}
                setRating={() => onStarRatingPress(3)}
              />
              <RatingStar
                isFill={rating >= 4}
                setRating={() => onStarRatingPress(4)}
              />
              <RatingStar
                isFill={rating >= 5}
                setRating={() => onStarRatingPress(5)}
              />
            </Box>
            <Box
              flexDirection="row"
              justifyContent="space-between"
              marginTop="sm"
            >
              <MainText variant="l4-medium" color="neutral.600">
                {i18n.t("rating-legend-low")}
              </MainText>
              <MainText variant="l4-medium" color="neutral.600">
                {i18n.t("rating-legend-high")}
              </MainText>
            </Box>
          </Box>
        </Box>
        <Box width={isWebDesktop ? "45%" : "100%"}>
          <MainButton
            isDisabled={rating === 0}
            onPress={() => navigation.navigate("RecapFeedbackScreen")}
            text={i18n.t("next")}
          />
        </Box>
      </MySafeAreaView>
    </WebDesktopView>
  );
};

export default ActionRatingScreen;
